/**
 * /field/ew/jobs/:id — one of the hand's Earthworks jobs (GET
 * /earthworks/field/my-jobs/:id, fieldiq-api#1284). SNAKE_CASE.
 *
 * CREW ONLY: the API answers 403 not_on_crew to anyone else, whatever their
 * role, and this screen says so in a sentence rather than as a load failure.
 * Rent-ins come without vendor rates (the field surface never sends them).
 * Warnings are shown first, in amber; they never stop the hand writing the
 * log. The one action is today's log, on the tenant's today from `meta.today`.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ClipboardList, FileText } from 'lucide-react';
import { useEwFieldJob } from '../../../hooks/useEwField';
import { queryFailureError } from '../../../lib/load-failure';
import { ewApiError, ewDay, ewJobStatusSkin, ewLocateSkin, ewRefusal, ewRentSkin } from '../../../lib/earthworks';
import {
  PT_FIELD_PRIMARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';
import { EwWarningCards, fromFieldWarning } from '../EwWarnings';
import { EwFieldBillingSections } from './EwFieldBillingSections';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
      <span className="text-sm text-fieldiq-muted">{label}</span>
      <span className="text-right text-sm font-semibold text-white">{value}</span>
    </div>
  );
}

/** A refusal that is the API's "not on this crew": a sentence, not a load failure. */
export function ewNotOnCrewMessage(query: { error?: unknown; failureReason?: unknown }): string | null {
  const err = queryFailureError(query);
  return ewApiError(err).code === 'not_on_crew' ? ewRefusal(err, '') : null;
}

export default function EwFieldJobScreen() {
  const { id } = useParams<{ id: string }>();
  const query = useEwFieldJob(id);
  const job = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const today = meta?.today ?? null;
  const refused = ewNotOnCrewMessage(query);
  const loggable = job?.status === 'scheduled' || job?.status === 'in_progress';
  const todayLog = job?.today_log ?? null;
  const units = (job?.equipment.length ?? 0) + (job?.rent_ins.length ?? 0);

  return (
    <PtFieldPage title={job?.job_number || 'Job'} backTo="/field/ew" testId="ew-field-job">
      {refused ? (
        <PtRefusal message={refused} testId="ew-field-not-on-crew" />
      ) : (
        <PtFieldQueryState query={query} subject="this job">
          {!job ? (
            <PtRefusal message="That job no longer exists. It may have been deleted." />
          ) : (
            <div className="space-y-4">
              <EwWarningCards warnings={(job.warnings ?? []).map(fromFieldWarning)} timeZone={meta?.time_zone ?? null} testId="ew-field-job-warnings" />

              <PtFieldCard>
                <div className="flex items-center justify-between gap-2 border-b border-fieldiq-border px-4 py-3">
                  <span className="text-base font-bold text-white">{job.customer_name || '—'}</span>
                  <PtFieldBadge skin={ewJobStatusSkin(job.status)} />
                </div>
                <Row label="Site" value={job.site_name || '—'} />
                <Row label="Job type" value={job.job_type_label || '—'} />
                <Row label="Supervisor" value={job.supervisor_name || '—'} />
                <Row label="Scheduled" value={`${ewDay(job.scheduled_start)} → ${ewDay(job.scheduled_end)}`} />
                {job.scope && <p className="px-4 py-3 text-sm text-fieldiq-text">{job.scope}</p>}
              </PtFieldCard>

              {loggable && today ? (
                <Link to={`/field/ew/jobs/${job.id}/log/${today}`} className={PT_FIELD_PRIMARY} data-testid="ew-field-today-log">
                  <ClipboardList className="size-5" aria-hidden="true" />
                  {todayLog ? `Today's log (${ewDay(today)})` : `Write today's log (${ewDay(today)})`}
                </Link>
              ) : (
                <p className="text-sm text-fieldiq-muted">{job.status === 'draft' ? 'This job is still a draft.' : 'This job takes no more logs.'}</p>
              )}
              {todayLog && (
                <p className="text-sm text-fieldiq-text" data-testid="ew-field-today-summary">
                  Today: {todayLog.crew.length} on crew hours, {todayLog.equipment.length} unit{todayLog.equipment.length === 1 ? '' : 's'}
                  {todayLog.updated_by_name ? ` · last saved by ${todayLog.updated_by_name}` : ''}
                </p>
              )}

              <section>
                <PtFieldSectionTitle>Locates · {job.locates.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {job.locates.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No utility locate is on file for this site.</p>
                  ) : (
                    job.locates.map((l) => (
                      <div key={l.id} data-testid="ew-field-locate" className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                        <span className="min-w-0 text-sm">
                          <span className="block truncate font-mono font-semibold text-white">{l.ticket_number || '—'}</span>
                          <span className="block truncate text-xs text-fieldiq-muted">
                            {l.provider || '—'} · {ewDay(l.valid_from)} → {ewDay(l.valid_to)}
                          </span>
                          {l.file?.url && (
                            <a href={l.file.url} target="_blank" rel="noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold text-fi-amber">
                              <FileText className="size-3.5" aria-hidden="true" />
                              Locate sheet
                            </a>
                          )}
                        </span>
                        <PtFieldBadge skin={ewLocateSkin(l.status)} />
                      </div>
                    ))
                  )}
                </PtFieldCard>
              </section>

              <section>
                <PtFieldSectionTitle>Crew · {job.crew.length}</PtFieldSectionTitle>
                <PtFieldCard>
                  {job.crew.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No crew.</p>
                  ) : (
                    job.crew.map((c) => <Row key={c.id} label={c.user_name || '—'} value={c.crew_role_label || c.crew_role || '—'} />)
                  )}
                </PtFieldCard>
              </section>

              <section>
                <PtFieldSectionTitle>Units · {units}</PtFieldSectionTitle>
                <PtFieldCard>
                  {units === 0 ? (
                    <p className="px-4 py-3 text-sm text-fieldiq-muted">No units on this job.</p>
                  ) : (
                    <>
                      {job.equipment.map((e) => (
                        <Row key={e.id} label={e.equipment_name || '—'} value={e.unit_number || 'Owned'} />
                      ))}
                      {job.rent_ins.map((r) => (
                        <div key={r.id} data-testid="ew-field-rent-in" className="flex items-center justify-between gap-3 border-b border-fieldiq-border px-4 py-2.5 last:border-b-0">
                          <span className="min-w-0 text-sm">
                            <span className="block truncate font-semibold text-white">{r.description || '—'}</span>
                            <span className="block truncate text-xs text-fieldiq-muted">
                              Rent-in · {r.vendor_name || '—'} · {ewDay(r.on_rent_on)} → {ewDay(r.off_rent_on)}
                            </span>
                          </span>
                          <PtFieldBadge skin={ewRentSkin(r.rent_status)} />
                        </div>
                      ))}
                    </>
                  )}
                </PtFieldCard>
              </section>

              <EwFieldBillingSections jobId={job.id} jobStatus={job.status} />
            </div>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
